import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { 
  Plus, Trash2, Edit2, Users, FolderOpen, FileText, 
  LogOut, Save, X, Music, Video, Image, FileDown,
  Loader2, RefreshCw, Upload, CheckCircle, MessageCircle, Quote, ImageIcon
} from "lucide-react";

const typeOptions = [
  { value: "audio", label: "Audio / Méditation", icon: Music },
  { value: "video", label: "Vidéo", icon: Video },
  { value: "text", label: "Texte long", icon: FileText },
  { value: "photo", label: "Photo / Galerie", icon: Image },
  { value: "document", label: "Document", icon: FileDown },
];

const pageOptions = [
  { value: "home", label: "Accueil COCON" },
  { value: "cocon-sacre", label: "Cocon Sacré - Bali" },
  { value: "valouse", label: "COCON - Valouse" },
];

const sectionOptions = [
  { value: "hero", label: "Image principale (Hero)" },
  { value: "gallery", label: "Galerie d'ambiance" },
  { value: "ambiance", label: "Vidéo d'ambiance" },
];

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("contents");
  const [sections, setSections] = useState([]);
  const [contents, setContents] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [pageMedia, setPageMedia] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingMedia, setEditingMedia] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);
  const mediaFileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    section_id: "",
    type: "audio",
    title: "",
    description: "",
    media_url: "",
    thumbnail_url: "",
    content_text: "",
    order: 1,
    is_published: true
  });
  
  const [testimonialFormData, setTestimonialFormData] = useState({
    author_name: "",
    testimonial_text: "",
    response_text: "",
    order: 1,
    is_published: true
  });

  const [mediaFormData, setMediaFormData] = useState({
    page: "home",
    section: "gallery",
    type: "image",
    url: "",
    alt_text: "",
    order: 1,
    is_published: true
  });
  
  const [newParticipantEmail, setNewParticipantEmail] = useState("");
  
  const navigate = useNavigate();
  const adminToken = sessionStorage.getItem("admin_token");
  const adminEmail = sessionStorage.getItem("admin_email");

  const headers = {
    "X-Admin-Token": adminToken
  };

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchAllData();
  }, [adminToken, navigate]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [sectionsRes, contentsRes, participantsRes, testimonialsRes, mediaRes] = await Promise.all([
        axios.get(`${API}/admin/sections`, { headers }),
        axios.get(`${API}/admin/contents`, { headers }),
        axios.get(`${API}/admin/participants`, { headers }),
        axios.get(`${API}/admin/testimonials`, { headers }),
        axios.get(`${API}/admin/page-media`, { headers })
      ]);
      setSections(sectionsRes.data);
      setContents(contentsRes.data);
      setParticipants(participantsRes.data);
      setTestimonials(testimonialsRes.data);
      setPageMedia(mediaRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expirée");
        sessionStorage.removeItem("admin_token");
        sessionStorage.removeItem("admin_email");
        navigate("/admin");
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const seedSections = async () => {
    try {
      await axios.post(`${API}/admin/seed`, {}, { headers });
      toast.success("Sections créées !");
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de la création des sections");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress("Envoi en cours...");
    
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (response.data.status === "uploaded") {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        setFormData({ ...formData, media_url: fullUrl });
        setUploadProgress("Fichier uploadé !");
        toast.success("Fichier uploadé avec succès");
        
        setTimeout(() => setUploadProgress(null), 2000);
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
      setUploadProgress(null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCreateContent = async (e) => {
    e.preventDefault();
    try {
      if (editingContent) {
        await axios.put(`${API}/admin/contents/${editingContent.id}`, formData, { headers });
        toast.success("Contenu mis à jour !");
      } else {
        await axios.post(`${API}/admin/contents`, formData, { headers });
        toast.success("Contenu créé !");
      }
      setShowForm(false);
      setEditingContent(null);
      resetForm();
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm("Supprimer ce contenu ?")) return;
    try {
      await axios.delete(`${API}/admin/contents/${id}`, { headers });
      toast.success("Contenu supprimé");
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleEditContent = (content) => {
    setEditingContent(content);
    setFormData({
      section_id: content.section_id,
      type: content.type,
      title: content.title,
      description: content.description || "",
      media_url: content.media_url || "",
      thumbnail_url: content.thumbnail_url || "",
      content_text: content.content_text || "",
      order: content.order,
      is_published: content.is_published
    });
    setShowForm(true);
  };

  const handleAddParticipant = async (e) => {
    e.preventDefault();
    if (!newParticipantEmail) return;
    try {
      await axios.post(`${API}/admin/participants`, { email: newParticipantEmail }, { headers });
      toast.success("Participante ajoutée !");
      setNewParticipantEmail("");
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDeleteParticipant = async (id) => {
    if (!window.confirm("Retirer cette participante ?")) return;
    try {
      await axios.delete(`${API}/admin/participants/${id}`, { headers });
      toast.success("Participante retirée");
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setFormData({
      section_id: sections[0]?.id || "",
      type: "audio",
      title: "",
      description: "",
      media_url: "",
      thumbnail_url: "",
      content_text: "",
      order: contents.length + 1,
      is_published: true
    });
    setUploadProgress(null);
  };

  // Testimonial handlers
  const resetTestimonialForm = () => {
    setTestimonialFormData({
      author_name: "",
      testimonial_text: "",
      response_text: "",
      order: testimonials.length + 1,
      is_published: true
    });
  };

  const handleCreateTestimonial = async (e) => {
    e.preventDefault();
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/admin/testimonials/${editingTestimonial.id}`, testimonialFormData, { headers });
        toast.success("Témoignage mis à jour !");
      } else {
        await axios.post(`${API}/admin/testimonials`, testimonialFormData, { headers });
        toast.success("Témoignage créé !");
      }
      setShowTestimonialForm(false);
      setEditingTestimonial(null);
      resetTestimonialForm();
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEditTestimonial = (testimonial) => {
    setEditingTestimonial(testimonial);
    setTestimonialFormData({
      author_name: testimonial.author_name,
      testimonial_text: testimonial.testimonial_text,
      response_text: testimonial.response_text || "",
      order: testimonial.order,
      is_published: testimonial.is_published
    });
    setShowTestimonialForm(true);
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers });
      toast.success("Témoignage supprimé");
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Media handlers
  const resetMediaForm = () => {
    setMediaFormData({
      page: "home",
      section: "gallery",
      type: "image",
      url: "",
      alt_text: "",
      order: pageMedia.filter(m => m.page === "home").length + 1,
      is_published: true
    });
  };

  const handleMediaFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (response.data.status === "uploaded") {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        setMediaFormData({ ...mediaFormData, url: fullUrl, type: response.data.category });
        toast.success("Fichier uploadé !");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setIsUploading(false);
      if (mediaFileInputRef.current) {
        mediaFileInputRef.current.value = "";
      }
    }
  };

  const handleCreateMedia = async (e) => {
    e.preventDefault();
    try {
      if (editingMedia) {
        await axios.put(`${API}/admin/page-media/${editingMedia.id}`, mediaFormData, { headers });
        toast.success("Visuel mis à jour !");
      } else {
        await axios.post(`${API}/admin/page-media`, mediaFormData, { headers });
        toast.success("Visuel ajouté !");
      }
      setShowMediaForm(false);
      setEditingMedia(null);
      resetMediaForm();
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEditMedia = (media) => {
    setEditingMedia(media);
    setMediaFormData({
      page: media.page,
      section: media.section,
      type: media.type,
      url: media.url,
      alt_text: media.alt_text || "",
      order: media.order,
      is_published: media.is_published
    });
    setShowMediaForm(true);
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm("Supprimer ce visuel ?")) return;
    try {
      await axios.delete(`${API}/admin/page-media/${id}`, { headers });
      toast.success("Visuel supprimé");
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { headers });
    } catch (e) { console.error('Logout request failed:', e); }
    sessionStorage.removeItem("admin_token");
    sessionStorage.removeItem("admin_email");
    navigate("/admin");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <header className="bg-white border-b border-[#C5A059]/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-xl text-[#3F2A24]">Admin - Cocon Sacré</h1>
            <p className="text-xs text-[#8C6A4A]">{adminEmail}</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchAllData}
              className="p-2 rounded-full hover:bg-[#C5A059]/10 transition-colors"
              title="Actualiser"
            >
              <RefreshCw className="w-5 h-5 text-[#4B3329]" strokeWidth={1.5} />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-full hover:bg-[#C5A059]/10 transition-colors"
              title="Déconnexion"
            >
              <LogOut className="w-5 h-5 text-[#4B3329]" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab("contents")}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "contents" 
                ? "bg-[#C5A059] text-white" 
                : "bg-white text-[#4B3329] hover:bg-[#C5A059]/10"
            }`}
            data-testid="tab-contents"
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Contenus ({contents.length})
          </button>
          <button
            onClick={() => setActiveTab("testimonials")}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "testimonials" 
                ? "bg-[#C5A059] text-white" 
                : "bg-white text-[#4B3329] hover:bg-[#C5A059]/10"
            }`}
            data-testid="tab-testimonials"
          >
            <Quote className="w-4 h-4 inline mr-2" />
            Témoignages ({testimonials.length})
          </button>
          <button
            onClick={() => setActiveTab("sections")}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "sections" 
                ? "bg-[#C5A059] text-white" 
                : "bg-white text-[#4B3329] hover:bg-[#C5A059]/10"
            }`}
            data-testid="tab-sections"
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            Sections ({sections.length})
          </button>
          <button
            onClick={() => setActiveTab("participants")}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "participants" 
                ? "bg-[#C5A059] text-white" 
                : "bg-white text-[#4B3329] hover:bg-[#C5A059]/10"
            }`}
            data-testid="tab-participants"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Participantes ({participants.length})
          </button>
          <button
            onClick={() => setActiveTab("media")}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "media" 
                ? "bg-[#C5A059] text-white" 
                : "bg-white text-[#4B3329] hover:bg-[#C5A059]/10"
            }`}
            data-testid="tab-media"
          >
            <ImageIcon className="w-4 h-4 inline mr-2" />
            Visuels ({pageMedia.length})
          </button>
        </div>

        {/* Sections Tab */}
        {activeTab === "sections" && (
          <div className="animate-fade-in-up">
            {sections.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#C5A059]/10">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 text-[#8C6A4A]" strokeWidth={1} />
                <p className="text-[#4B3329] mb-4">Aucune section créée</p>
                <button
                  onClick={seedSections}
                  className="btn-sacred"
                  data-testid="seed-sections-btn"
                >
                  Créer les sections de base
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {sections.map((section) => (
                  <div key={section.id} className="admin-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-serif text-lg text-[#3F2A24]">{section.name}</h3>
                        <p className="text-sm text-[#8C6A4A]">{section.description}</p>
                        <p className="text-xs text-[#C5A059] mt-1">/{section.slug}</p>
                      </div>
                      <div className="text-sm text-[#8C6A4A]">
                        Ordre: {section.order}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contents Tab */}
        {activeTab === "contents" && (
          <div className="animate-fade-in-up">
            {/* Add Content Button */}
            <div className="mb-6">
              <button
                onClick={() => {
                  resetForm();
                  setEditingContent(null);
                  setShowForm(true);
                }}
                className="btn-sacred flex items-center gap-2"
                data-testid="add-content-btn"
              >
                <Plus className="w-4 h-4" />
                Ajouter un contenu
              </button>
            </div>

            {/* Content Form Modal */}
            {showForm && (
              <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-xl text-[#3F2A24]">
                      {editingContent ? "Modifier le contenu" : "Nouveau contenu"}
                    </h2>
                    <button
                      onClick={() => {
                        setShowForm(false);
                        setEditingContent(null);
                        setUploadProgress(null);
                      }}
                      className="p-2 hover:bg-[#C5A059]/10 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-[#4B3329]" />
                    </button>
                  </div>

                  <form onSubmit={handleCreateContent} className="space-y-4">
                    {/* Section */}
                    <div className="form-group">
                      <label className="form-label">Section</label>
                      <select
                        value={formData.section_id}
                        onChange={(e) => setFormData({ ...formData, section_id: e.target.value })}
                        className="form-input"
                        required
                        data-testid="content-section-select"
                      >
                        <option value="">Choisir une section</option>
                        {sections.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Type */}
                    <div className="form-group">
                      <label className="form-label">Type de contenu</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="form-input"
                        required
                        data-testid="content-type-select"
                      >
                        {typeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Title */}
                    <div className="form-group">
                      <label className="form-label">Titre</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="form-input"
                        required
                        data-testid="content-title-input"
                      />
                    </div>

                    {/* Description */}
                    <div className="form-group">
                      <label className="form-label">Description (optionnel)</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="form-input form-textarea"
                        rows={2}
                        data-testid="content-description-input"
                      />
                    </div>

                    {/* File Upload */}
                    {formData.type !== "text" && (
                      <div className="form-group">
                        <label className="form-label">
                          Uploader un fichier
                        </label>
                        <div className="border-2 border-dashed border-[#C5A059]/20 rounded-xl p-4 text-center hover:border-[#C5A059]/40 transition-colors">
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileUpload}
                            accept={
                              formData.type === "audio" ? "audio/*" :
                              formData.type === "video" ? "video/*" :
                              formData.type === "photo" ? "image/*" :
                              "*/*"
                            }
                            className="hidden"
                            id="file-upload"
                            data-testid="file-upload-input"
                          />
                          <label 
                            htmlFor="file-upload"
                            className="cursor-pointer flex flex-col items-center gap-2"
                          >
                            {isUploading ? (
                              <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
                            ) : uploadProgress ? (
                              <CheckCircle className="w-8 h-8 text-[#C5A059]" />
                            ) : (
                              <Upload className="w-8 h-8 text-[#8C6A4A]" strokeWidth={1.5} />
                            )}
                            <span className="text-sm text-[#4B3329]">
                              {uploadProgress || "Cliquer pour uploader"}
                            </span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Media URL */}
                    {formData.type !== "text" && (
                      <div className="form-group">
                        <label className="form-label">
                          Ou URL du média (SoundCloud, Vimeo, lien direct...)
                        </label>
                        <input
                          type="url"
                          value={formData.media_url}
                          onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                          className="form-input"
                          placeholder="https://..."
                          data-testid="content-media-url-input"
                        />
                      </div>
                    )}

                    {/* Thumbnail */}
                    <div className="form-group">
                      <label className="form-label">URL de la miniature (optionnel)</label>
                      <input
                        type="url"
                        value={formData.thumbnail_url}
                        onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                        className="form-input"
                        placeholder="https://..."
                        data-testid="content-thumbnail-input"
                      />
                    </div>

                    {/* Long Text Content */}
                    {(formData.type === "text" || formData.type === "audio") && (
                      <div className="form-group">
                        <label className="form-label">Contenu texte</label>
                        <textarea
                          value={formData.content_text}
                          onChange={(e) => setFormData({ ...formData, content_text: e.target.value })}
                          className="form-input form-textarea"
                          rows={6}
                          placeholder="Ton texte ici..."
                          data-testid="content-text-input"
                        />
                      </div>
                    )}

                    {/* Order */}
                    <div className="form-group">
                      <label className="form-label">Ordre d'affichage</label>
                      <input
                        type="number"
                        value={formData.order}
                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                        className="form-input"
                        min={1}
                        data-testid="content-order-input"
                      />
                    </div>

                    {/* Published */}
                    <div className="form-group">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_published}
                          onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                          className="w-5 h-5 rounded border-[#C5A059]/30 text-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="content-published-checkbox"
                        />
                        <span className="text-sm text-[#3F2A24]">Publié (visible par les participantes)</span>
                      </label>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForm(false);
                          setEditingContent(null);
                          setUploadProgress(null);
                        }}
                        className="btn-sacred-outline flex-1"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="btn-sacred flex-1 flex items-center justify-center gap-2"
                        data-testid="save-content-btn"
                      >
                        <Save className="w-4 h-4" />
                        Enregistrer
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Contents List */}
            {contents.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#C5A059]/10">
                <FileText className="w-12 h-12 mx-auto mb-4 text-[#8C6A4A]" strokeWidth={1} />
                <p className="text-[#4B3329]">Aucun contenu créé</p>
                <p className="text-sm text-[#8C6A4A] mt-2">
                  {sections.length === 0 
                    ? "Créez d'abord les sections avant d'ajouter des contenus"
                    : "Cliquez sur 'Ajouter un contenu' pour commencer"
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sections.map((section) => {
                  const sectionContents = contents.filter(c => c.section_id === section.id);
                  if (sectionContents.length === 0) return null;
                  
                  return (
                    <div key={section.id} className="mb-6">
                      <h3 className="text-sm font-medium text-[#8C6A4A] uppercase tracking-wide mb-3">
                        {section.name} ({sectionContents.length})
                      </h3>
                      <div className="space-y-2">
                        {sectionContents.map((content) => {
                          const TypeIcon = typeOptions.find(t => t.value === content.type)?.icon || FileText;
                          return (
                            <div 
                              key={content.id} 
                              className={`admin-card flex items-center gap-4 ${!content.is_published ? 'opacity-50' : ''}`}
                            >
                              <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                                <TypeIcon className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-[#3F2A24] truncate">{content.title}</h4>
                                <p className="text-xs text-[#8C6A4A]">
                                  {typeOptions.find(t => t.value === content.type)?.label} • Ordre: {content.order}
                                  {!content.is_published && " • Non publié"}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditContent(content)}
                                  className="p-2 hover:bg-[#C5A059]/10 rounded-full transition-colors"
                                  title="Modifier"
                                  data-testid={`edit-content-${content.id}`}
                                >
                                  <Edit2 className="w-4 h-4 text-[#4B3329]" strokeWidth={1.5} />
                                </button>
                                <button
                                  onClick={() => handleDeleteContent(content.id)}
                                  className="p-2 hover:bg-red-50 rounded-full transition-colors"
                                  title="Supprimer"
                                  data-testid={`delete-content-${content.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-400" strokeWidth={1.5} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Participants Tab */}
        {activeTab === "participants" && (
          <div className="animate-fade-in-up">
            {/* Add Participant Form */}
            <form onSubmit={handleAddParticipant} className="mb-6 flex gap-3">
              <input
                type="email"
                value={newParticipantEmail}
                onChange={(e) => setNewParticipantEmail(e.target.value)}
                placeholder="email@exemple.com"
                className="form-input flex-1"
                data-testid="new-participant-email"
              />
              <button
                type="submit"
                className="btn-sacred flex items-center gap-2"
                data-testid="add-participant-btn"
              >
                <Plus className="w-4 h-4" />
                Ajouter
              </button>
            </form>

            {/* Participants List */}
            {participants.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#C5A059]/10">
                <Users className="w-12 h-12 mx-auto mb-4 text-[#8C6A4A]" strokeWidth={1} />
                <p className="text-[#4B3329]">Aucune participante</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#C5A059]/10 overflow-hidden">
                <table className="admin-table">
                  <thead>
                    <tr className="bg-[#F9F7F2]">
                      <th>Email</th>
                      <th>Vérifiée</th>
                      <th>Dernier accès</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p.id}>
                        <td className="font-medium">{p.email}</td>
                        <td>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                            p.verified 
                              ? 'bg-[#C5A059]/10 text-[#C5A059]' 
                              : 'bg-[#D48C8C]/10 text-[#D48C8C]'
                          }`}>
                            {p.verified ? 'Oui' : 'Non'}
                          </span>
                        </td>
                        <td className="text-[#8C6A4A] text-sm">
                          {p.last_access 
                            ? new Date(p.last_access).toLocaleDateString('fr-FR', { 
                                day: 'numeric', 
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : '-'
                          }
                        </td>
                        <td>
                          <button
                            onClick={() => handleDeleteParticipant(p.id)}
                            className="p-2 hover:bg-red-50 rounded-full transition-colors"
                            title="Retirer"
                            data-testid={`delete-participant-${p.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" strokeWidth={1.5} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === "testimonials" && (
          <div className="animate-fade-in-up">
            {/* Add Testimonial Button */}
            <div className="mb-6">
              <button
                onClick={() => {
                  resetTestimonialForm();
                  setEditingTestimonial(null);
                  setShowTestimonialForm(true);
                }}
                className="btn-sacred flex items-center gap-2"
                data-testid="add-testimonial-btn"
              >
                <Plus className="w-4 h-4" />
                Ajouter un témoignage
              </button>
            </div>

            {/* Testimonial Form Modal */}
            {showTestimonialForm && (
              <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-xl text-[#3F2A24]">
                      {editingTestimonial ? "Modifier le témoignage" : "Nouveau témoignage"}
                    </h2>
                    <button
                      onClick={() => {
                        setShowTestimonialForm(false);
                        setEditingTestimonial(null);
                      }}
                      className="p-2 hover:bg-[#C5A059]/10 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-[#4B3329]" />
                    </button>
                  </div>

                  <form onSubmit={handleCreateTestimonial} className="space-y-4">
                    {/* Author Name */}
                    <div className="form-group">
                      <label className="form-label">Prénom / Nom</label>
                      <input
                        type="text"
                        value={testimonialFormData.author_name}
                        onChange={(e) => setTestimonialFormData({ ...testimonialFormData, author_name: e.target.value })}
                        className="form-input"
                        placeholder="Marie"
                        required
                        data-testid="testimonial-author-input"
                      />
                    </div>

                    {/* Testimonial Text */}
                    <div className="form-group">
                      <label className="form-label">Témoignage</label>
                      <textarea
                        value={testimonialFormData.testimonial_text}
                        onChange={(e) => setTestimonialFormData({ ...testimonialFormData, testimonial_text: e.target.value })}
                        className="form-input form-textarea"
                        rows={6}
                        placeholder="Son témoignage tel qu'elle l'a écrit..."
                        required
                        data-testid="testimonial-text-input"
                      />
                    </div>

                    {/* Response Text */}
                    <div className="form-group">
                      <label className="form-label">Ta réponse personnalisée</label>
                      <textarea
                        value={testimonialFormData.response_text}
                        onChange={(e) => setTestimonialFormData({ ...testimonialFormData, response_text: e.target.value })}
                        className="form-input form-textarea"
                        rows={4}
                        placeholder="Ta réponse qui apparaîtra sous le témoignage..."
                        data-testid="testimonial-response-input"
                      />
                    </div>

                    {/* Order */}
                    <div className="form-group">
                      <label className="form-label">Ordre d'affichage</label>
                      <input
                        type="number"
                        value={testimonialFormData.order}
                        onChange={(e) => setTestimonialFormData({ ...testimonialFormData, order: parseInt(e.target.value) })}
                        className="form-input"
                        min={1}
                        data-testid="testimonial-order-input"
                      />
                    </div>

                    {/* Published */}
                    <div className="form-group">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={testimonialFormData.is_published}
                          onChange={(e) => setTestimonialFormData({ ...testimonialFormData, is_published: e.target.checked })}
                          className="w-5 h-5 rounded border-[#C5A059]/30 text-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="testimonial-published-checkbox"
                        />
                        <span className="text-sm text-[#3F2A24]">Publié (visible sur la page)</span>
                      </label>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowTestimonialForm(false);
                          setEditingTestimonial(null);
                        }}
                        className="btn-sacred-outline flex-1"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="btn-sacred flex-1 flex items-center justify-center gap-2"
                        data-testid="save-testimonial-btn"
                      >
                        <Save className="w-4 h-4" />
                        Enregistrer
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Testimonials List */}
            {testimonials.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#C5A059]/10">
                <Quote className="w-12 h-12 mx-auto mb-4 text-[#8C6A4A]" strokeWidth={1} />
                <p className="text-[#4B3329]">Aucun témoignage</p>
                <p className="text-sm text-[#8C6A4A] mt-2">
                  Cliquez sur 'Ajouter un témoignage' pour commencer
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {testimonials.map((testimonial) => (
                  <div 
                    key={testimonial.id} 
                    className={`admin-card ${!testimonial.is_published ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                        <Quote className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-[#3F2A24]">{testimonial.author_name}</h4>
                        <p className="text-sm text-[#4B3329] mt-1 line-clamp-2">
                          {testimonial.testimonial_text}
                        </p>
                        {testimonial.response_text && (
                          <p className="text-xs text-[#C5A059] mt-2 italic">
                            + Ta réponse
                          </p>
                        )}
                        <p className="text-xs text-[#8C6A4A] mt-2">
                          Ordre: {testimonial.order}
                          {!testimonial.is_published && " • Non publié"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditTestimonial(testimonial)}
                          className="p-2 hover:bg-[#C5A059]/10 rounded-full transition-colors"
                          title="Modifier"
                          data-testid={`edit-testimonial-${testimonial.id}`}
                        >
                          <Edit2 className="w-4 h-4 text-[#4B3329]" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => handleDeleteTestimonial(testimonial.id)}
                          className="p-2 hover:bg-red-50 rounded-full transition-colors"
                          title="Supprimer"
                          data-testid={`delete-testimonial-${testimonial.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Media Tab */}
        {activeTab === "media" && (
          <div className="animate-fade-in-up">
            {/* Add Media Button */}
            <div className="mb-6">
              <button
                onClick={() => {
                  resetMediaForm();
                  setEditingMedia(null);
                  setShowMediaForm(true);
                }}
                className="btn-sacred flex items-center gap-2"
                data-testid="add-media-btn"
              >
                <Plus className="w-4 h-4" />
                Ajouter un visuel
              </button>
            </div>

            {/* Media Form Modal */}
            {showMediaForm && (
              <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-xl text-[#3F2A24]">
                      {editingMedia ? "Modifier le visuel" : "Nouveau visuel"}
                    </h2>
                    <button
                      onClick={() => {
                        setShowMediaForm(false);
                        setEditingMedia(null);
                      }}
                      className="p-2 hover:bg-[#C5A059]/10 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-[#4B3329]" />
                    </button>
                  </div>

                  <form onSubmit={handleCreateMedia} className="space-y-4">
                    {/* Page */}
                    <div className="form-group">
                      <label className="form-label">Page</label>
                      <select
                        value={mediaFormData.page}
                        onChange={(e) => setMediaFormData({ ...mediaFormData, page: e.target.value })}
                        className="form-input"
                        required
                        data-testid="media-page-select"
                      >
                        {pageOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Section */}
                    <div className="form-group">
                      <label className="form-label">Section</label>
                      <select
                        value={mediaFormData.section}
                        onChange={(e) => setMediaFormData({ ...mediaFormData, section: e.target.value })}
                        className="form-input"
                        required
                        data-testid="media-section-select"
                      >
                        {sectionOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* File Upload */}
                    <div className="form-group">
                      <label className="form-label">Uploader un fichier</label>
                      <div className="border-2 border-dashed border-[#C5A059]/20 rounded-xl p-4 text-center hover:border-[#C5A059]/40 transition-colors">
                        <input
                          ref={mediaFileInputRef}
                          type="file"
                          onChange={handleMediaFileUpload}
                          accept="image/*,video/*"
                          className="hidden"
                          id="media-file-upload"
                          data-testid="media-file-upload-input"
                        />
                        <label 
                          htmlFor="media-file-upload"
                          className="cursor-pointer flex flex-col items-center gap-2"
                        >
                          {isUploading ? (
                            <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
                          ) : (
                            <Upload className="w-8 h-8 text-[#8C6A4A]" strokeWidth={1.5} />
                          )}
                          <span className="text-sm text-[#4B3329]">
                            {isUploading ? "Envoi en cours..." : "Cliquer pour uploader"}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* URL */}
                    <div className="form-group">
                      <label className="form-label">Ou URL de l'image/vidéo</label>
                      <input
                        type="url"
                        value={mediaFormData.url}
                        onChange={(e) => setMediaFormData({ ...mediaFormData, url: e.target.value })}
                        className="form-input"
                        placeholder="https://..."
                        required
                        data-testid="media-url-input"
                      />
                    </div>

                    {/* Preview */}
                    {mediaFormData.url && (
                      <div className="form-group">
                        <label className="form-label">Aperçu</label>
                        <div className="rounded-xl overflow-hidden h-32 bg-[#EAE8E1]">
                          <img 
                            src={mediaFormData.url} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      </div>
                    )}

                    {/* Alt Text */}
                    <div className="form-group">
                      <label className="form-label">Description (optionnel)</label>
                      <input
                        type="text"
                        value={mediaFormData.alt_text}
                        onChange={(e) => setMediaFormData({ ...mediaFormData, alt_text: e.target.value })}
                        className="form-input"
                        placeholder="Description de l'image"
                        data-testid="media-alt-input"
                      />
                    </div>

                    {/* Order */}
                    <div className="form-group">
                      <label className="form-label">Ordre d'affichage</label>
                      <input
                        type="number"
                        value={mediaFormData.order}
                        onChange={(e) => setMediaFormData({ ...mediaFormData, order: parseInt(e.target.value) })}
                        className="form-input"
                        min={1}
                        data-testid="media-order-input"
                      />
                    </div>

                    {/* Published */}
                    <div className="form-group">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={mediaFormData.is_published}
                          onChange={(e) => setMediaFormData({ ...mediaFormData, is_published: e.target.checked })}
                          className="w-5 h-5 rounded border-[#C5A059]/30 text-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="media-published-checkbox"
                        />
                        <span className="text-sm text-[#3F2A24]">Publié (visible sur la page)</span>
                      </label>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowMediaForm(false);
                          setEditingMedia(null);
                        }}
                        className="btn-sacred-outline flex-1"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="btn-sacred flex-1 flex items-center justify-center gap-2"
                        data-testid="save-media-btn"
                      >
                        <Save className="w-4 h-4" />
                        Enregistrer
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Media List */}
            {pageMedia.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#C5A059]/10">
                <ImageIcon className="w-12 h-12 mx-auto mb-4 text-[#8C6A4A]" strokeWidth={1} />
                <p className="text-[#4B3329]">Aucun visuel</p>
                <p className="text-sm text-[#8C6A4A] mt-2">
                  Ajoutez des photos et vidéos pour les pages publiques
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {pageOptions.map(pageOpt => {
                  const pageItems = pageMedia.filter(m => m.page === pageOpt.value);
                  if (pageItems.length === 0) return null;
                  
                  return (
                    <div key={pageOpt.value}>
                      <h3 className="text-sm font-medium text-[#8C6A4A] uppercase tracking-wide mb-3">
                        {pageOpt.label} ({pageItems.length})
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {pageItems.map((media) => (
                          <div 
                            key={media.id} 
                            className={`relative group rounded-xl overflow-hidden ${!media.is_published ? 'opacity-50' : ''}`}
                          >
                            <div className="aspect-video bg-[#EAE8E1]">
                              {media.type === 'video' ? (
                                <video src={media.url} className="w-full h-full object-cover" />
                              ) : (
                                <img 
                                  src={media.url} 
                                  alt={media.alt_text || "Visuel"} 
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                              <button
                                onClick={() => handleEditMedia(media)}
                                className="p-2 bg-white rounded-full hover:bg-[#C5A059]/10 transition-colors"
                                title="Modifier"
                              >
                                <Edit2 className="w-4 h-4 text-[#4B3329]" strokeWidth={1.5} />
                              </button>
                              <button
                                onClick={() => handleDeleteMedia(media.id)}
                                className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" strokeWidth={1.5} />
                              </button>
                            </div>
                            <div className="absolute bottom-2 left-2 right-2">
                              <span className="text-xs bg-white/90 px-2 py-1 rounded-full text-[#4B3329]">
                                {sectionOptions.find(s => s.value === media.section)?.label || media.section}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
